/* ============================================================
   splash-gate-video.js — M-25's background video, and nothing else.

   Split out of splash-gate.js on 2026-08-09 under the raw-byte per-file cap
   theme check enforced at the time. Why it was this half, how the two files are ordered,
   and what the split cost the per-page budget: sections/splash-gate.liquid's
   comment above the two script tags. That comment is stripped server-side and
   costs a visitor nothing, which is where reasoning belongs when the file
   holding it is metered.

   THE CONTRACT: one function on `window`, called once from the gate's
   connectedCallback and guarded there. Absent, blocked or still in flight,
   the plate image underneath is what renders — the same thing a visitor with
   no JavaScript gets. Nothing waits on this, and every failure path puts the
   still back, because the still never went away.

   ⚠ IT WRITES NO STATE ONTO THE ELEMENT. `data-playing` goes on the <video>,
   and splash-gate.js's activate() reads it back off the DOM rather than off a
   property this file set. A JS property shared across two files is what rots
   when one is edited alone; an attribute both can see is not.

   THE PRE-CONDITIONS ARE BACK (2026-08-09), and the fault they were removed to
   isolate is closed. They came out on 2026-08-07 to find out why the video
   "never started". It never started because the <video> is nested inside the
   section's `bg_image != blank` branch and no background image was set, so
   there was no element to start — unrelated to any of them. Two days of every
   visitor, including reduced-motion ones, autoplaying an HD file bought a
   diagnosis the markup could have given. The section now says so in the theme
   editor instead (nmp.editor.splash_video_no_image), which is the check that
   should have existed first. docs/03 §M-25 items 7-8 hold the reasoning for
   each gate, including why '3g' must NOT go back into the connection test.

   Two things from the diagnostic were kept because they were real fixes, not
   scaffolding: 'playing' rather than 'canplay' below, and naming every exit.

   Last updated: 2026-09-07
   ============================================================ */
(function () {
  'use strict';

  /* Save-Data or a genuinely slow connection. '3g' is deliberately absent —
     docs/03 §M-25: it covers a large share of real mobile traffic that streams
     video perfectly well, and excluding it made the check punish the wrong
     people. */
  function thrifty() {
    var conn = navigator.connection || {};
    return conn.saveData === true || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
  }

  /* warn, not log: this is a fallback the owner may not have intended. Every
     exit names itself, because a blocked request, a bad URL and a failed
     pre-check otherwise present identically — no element, no error, no
     request — which is what cost the 2026-08-07 session. */
  function noVideo(video, why, expected) {
    video.remove();
    /* The under-1024 exit is the designed outcome for every phone, not a
       fallback the owner may not have intended — so it alone stays quiet
       (2026-09-07); a permanent warning on the majority of traffic is noise
       that hides the four exits worth reading. */
    if (!expected && window.console) console.warn('[M-25] no background video — ' + why);
  }

  /* `root` is the <nmp-splash-gate> element. Called once per connect.
     Computes its own media state rather than reading the gate's properties:
     three matchMedia calls are cheaper than a contract between two files. */
  window.NmpSplashVideo = function (root) {
    var video = root.querySelector('[data-splash-video]');
    /* No element at all is the one failure this file cannot explain from here,
       and the likeliest one — see the header. The section warns in the editor. */
    if (!video) return;

    var why = !window.matchMedia('(min-width: 1024px)').matches
      ? 'viewport under 1024px at load'
      : !window.matchMedia('(hover: hover) and (pointer: fine)').matches
        ? 'no fine pointer'
        : window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'reduced motion requested'
          : thrifty()
            ? 'Save-Data on, or a 2g connection'
            : '';
    if (why) {
      noVideo(video, why, why.indexOf('viewport') === 0);
      return;
    }

    /* 'playing', NOT 'canplay'. canplay fires when enough has buffered,
       whether or not playback was ALLOWED — so revealing on it put a frozen
       first frame over the plate for anyone whose autoplay was refused. Found
       during the 2026-08-07 investigation and kept. */
    video.addEventListener('playing', function () {
      video.setAttribute('data-playing', '');
    }, { once: true });

    video.addEventListener('error', function () {
      noVideo(video, 'the file failed to load (URL or content blocker)');
    });

    /* preload="none" plus no src means nothing has been requested until here. */
    video.src = video.getAttribute('data-src');
    var p = video.play();
    if (p && p.catch) {
      p.catch(function (err) {
        noVideo(video, 'play() rejected: ' + err.name);
      });
    }
  };
})();
